import type { Group, Question } from '@api/instant_survey/types';
import { Button, CopyToClipboard, Error, Loader, ModalV2 as Modal, Select } from '@utilities';
import type { ErrorProps } from '@utilities/components/Error';
import {
  getSelectNumericValue,
  getSelectStringValue,
} from '@utilities/components/Select/utilities/helpers';
import { GlobeIcon, RepromptIcon } from '@utilities/icons';
import type { ModalProps } from '@utilities/components/ModalV2';
import { getSurveyLanguageDropdownOptions } from '@helpers';

import {
  getDropdownOptions,
  type PreviewSelection,
  useCanPreviewSurveys,
  useIframeUrl,
  usePublicLink,
} from '../../utilities';

import styles from './_index.module.scss';

export const PREVIEW_MODAL_ARIA_LABEL = 'Instant Survey Preview';

export interface PreviewModalProps extends Omit<ModalProps, 'children'> {
  country?: string;
  instantSurveyId: number;
  languages?: string[];
  onLanguageChange?: (language: string) => void;
  onQuestionChange: (questionId: number) => void;
  onQuotaGroupChange: (quotaGroupId: number) => void;
  onRestart: () => void;
  previewSelection: PreviewSelection;
  questions: Question[];
  quotaGroups: Group[];
}

type ErrorStatus = ErrorProps['status'];

const PreviewModal = ({
  country,
  instantSurveyId,
  languages = [],
  onLanguageChange,
  onQuestionChange,
  onQuotaGroupChange,
  onRestart,
  previewSelection = {},
  questions,
  quotaGroups,
  ...props
}: PreviewModalProps) => {
  const { language, questionId, quotaGroupId } = previewSelection;
  const { error, iframeUrl, isLoading } = useIframeUrl({
    country,
    instantSurveyId,
    previewSelection,
  });
  const { isLoading: isLoadingPublicLink, publicLink } = usePublicLink({
    country,
    instantSurveyId,
    quotaGroupId,
  });

  const canPreview = useCanPreviewSurveys();
  if (!canPreview) return null;

  const showQuotaGroupDropdown = quotaGroups.length > 1;

  const quotaGroupOptions = getDropdownOptions(quotaGroups, language);
  const questionOptions = getDropdownOptions(questions, language);

  const showLanguageDropdown = languages.length > 1;
  const languageOptions = getSurveyLanguageDropdownOptions(languages);

  return (
    <Modal
      aria-label={PREVIEW_MODAL_ARIA_LABEL}
      className={styles['preview-modal']}
      hasCloseIcon
      isDismissible
      size="fullscreen"
      {...props}
      shouldSlide
    >
      <div className={styles['preview-modal-header']}>
        <div className={styles['preview-modal-header-toolbar']}>
          <div className={styles['preview-modal-header-dropdowns']}>
            {showQuotaGroupDropdown && (
              <Select
                className={styles['preview-modal-quota-group-dropdown']}
                onChange={(option) => {
                  const value = getSelectNumericValue(option);
                  if (value != null) onQuotaGroupChange(value);
                }}
                options={quotaGroupOptions}
                value={quotaGroupOptions.find(
                  (option) => String(option.value) === String(quotaGroupId)
                )}
              />
            )}
            {showQuotaGroupDropdown && (
              <span className={styles['preview-modal-vertical-divider']} />
            )}
            <Select
              className={styles['preview-modal-question-dropdown']}
              onChange={(option) => {
                const value = getSelectNumericValue(option);
                if (value != null) onQuestionChange(value);
              }}
              options={questionOptions}
              value={questionOptions.find((option) => option.value === questionId)}
            />
          </div>
          <Button
            className={styles['preview-modal-restart-button']}
            icon={<RepromptIcon />}
            onClick={onRestart}
            text="Restart"
          />
        </div>
        <div className={styles['preview-modal-header-actions']}>
          {showLanguageDropdown && (
            <Select
              aria-label="Preview language"
              className={styles['preview-modal-language-dropdown']}
              onChange={(option) => {
                const value = getSelectStringValue(option);
                if (value != null) onLanguageChange?.(value);
              }}
              options={languageOptions}
              value={languageOptions.find((option) => option.value === language)}
            />
          )}
          <CopyToClipboard
            className={styles['preview-modal-public-link-button']}
            finalTooltipText="Copied!"
            initialContent={<GlobeIcon />}
            isDisabled={isLoadingPublicLink}
            text="Public Link"
            value={publicLink}
          />
        </div>
      </div>
      <div className={styles['preview-modal-content']}>
        <div className={styles['preview-modal-viewport']}>
          <div className={styles['preview-modal-mobile-device-frame']}>
            <Iframe error={error} isLoading={isLoading} src={iframeUrl} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface IframeProps {
  error?: ErrorStatus;
  isLoading: boolean;
  src: null | string;
}

const Iframe = ({ error, isLoading, src }: IframeProps) => {
  if (error) {
    return (
      <div className={styles['preview-modal-error']}>
        <Error status={error} />
      </div>
    );
  }

  if (isLoading || !src) {
    return (
      <div className={styles['preview-modal-loader']}>
        <Loader isCentered isVerticallyCentered />
      </div>
    );
  }

  return (
    <iframe
      allow="camera; microphone; clipboard-write"
      className={styles['preview-modal-iframe']}
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-popups allow-scripts allow-same-origin"
      src={src}
      title={PREVIEW_MODAL_ARIA_LABEL}
    />
  );
};

export default PreviewModal;
